<template>
  <div class="relative w-full h-full flex flex-col">
    <div v-for="type in ['payment', 'invoice']" class="flex-col">
      <h2 class="font-medium mb-4 text-right"> ספקי {{type === 'payment' ? 'סליקה' : 'חשבוניות'}} </h2>
      <div
        v-for="provider in providers.filter(x => Object.values(type === 'payment' ? PaymentProvider : InvoiceProvider).includes(x.id))"
        :key="provider.id"
        class="bg-white p-4 rounded-2xl border border-gray-100 flex items-center gap-4 mb-3"
      >
        <div class="w-12 h-12 rounded-xl border border-gray-100 flex items-center justify-center p-2 bg-white">
          <img :src="getImageUrl(provider.logo)"
               :alt="provider.name"
               class="w-full h-full object-contain" />
        </div>

        <div class="flex-1 min-w-0 text-right">
          <h3 class="font-bold text-gray-900 text-[16px]">{{ provider.name }}</h3>
          <p class="text-xs text-gray-500 leading-snug line-clamp-2 mt-0.5">{{ provider.desc }}</p>
        </div>

        <div>
          <Button
            v-if="!provider.connected"
            label="חיבור"
            size="small"
            severity="secondary"
            rounded
            @click="startIntegration(provider)"
            class="!px-5 !py-1.5 !text-sm !font-bold !bg-gray-100 hover:!bg-gray-200 !text-gray-900 !border-none"
          />
          <div v-else
               class="flex items-center gap-1.5 text-green-600 bg-green-50 px-3 py-1.5 rounded-full border border-green-100">
            <span class="text-xs font-bold">מחובר</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
              <path d="M20 6 9 17l-5-5" />
            </svg>
          </div>
        </div>
      </div>
    </div>
  </div>

</template>

<script setup lang="ts">
import { computed, markRaw, ref } from 'vue';
import HypInstructions from '@app/components/admin/components/payment-integrations/HypInstructions.vue';
import { useAdminStore } from '@app/stores';
import { InvoiceProvider, PaymentProvider } from '@torim-app/types';
import PaymentIntegrationSuccessStep
  from '@app/components/admin/components/payment-integrations/PaymentIntegrationSuccessStep.vue';
import HypFields from '@app/components/admin/components/payment-integrations/HypFields.vue';
import { Provider } from '@app/types/payments-integrations';
import EzCountInstructions from '@app/components/admin/components/payment-integrations/EzCountInstructions.vue';
import EzCountFields from '@app/components/admin/components/payment-integrations/EzCountFields.vue';
import SmartBeeInstructions from '@app/components/admin/components/payment-integrations/SmartBeeInstructions.vue';
import SmartBeeFields from '@app/components/admin/components/payment-integrations/SmartBeeFields.vue';
import PaymeInstructions from '@app/components/admin/components/payment-integrations/PaymeInstructions.vue';
import PaymeFields from '@app/components/admin/components/payment-integrations/PaymeFields.vue';
import PaymeInvoicesInstructions
  from '@app/components/admin/components/payment-integrations/PaymeInvoicesInstructions.vue';
import PaymeInvoicesValidate from '@app/components/admin/components/payment-integrations/PaymeInvoicesValidate.vue';
import PaymentIntegrationFlow
  from '@app/components/admin/components/payment-integrations/PaymentIntegrationFlow.vue';
import { useDrawer } from '@torim-app/shared-components';
import { trackEvent } from '@app/composables/posthog';

const adminStore = useAdminStore();
const { openDrawer } = useDrawer();

const providers = computed<Provider[]>(() => {
  return [
    {
      id: PaymentProvider.PAYME,
      name: 'טיימאיט (Payme)',
      desc: 'הסליקה הטובה והמשתלמת ביותר ללקוחות טיימאיט!',
      logo: 'logo-sign.svg',
      connected: adminStore.businessSiteData?.paymentIntegration?.paymentConfig?.provider === PaymentProvider.PAYME,
      flow: [markRaw(PaymeInstructions), markRaw(PaymeFields), markRaw(PaymentIntegrationSuccessStep)]
    },
    {
      id: PaymentProvider.HYP,
      name: 'Hyp (Yaad)',
      desc: 'ספק סליקה ותשלומים אונליין',
      logo: 'hyp_logo.svg',
      connected: adminStore.businessSiteData?.paymentIntegration?.paymentConfig?.provider === PaymentProvider.HYP,
      flow: [markRaw(HypInstructions), markRaw(HypFields), markRaw(PaymentIntegrationSuccessStep)]
    },
    {
      id: InvoiceProvider.PAYME,
      name: 'טיימאיט (Payme)',
      desc: 'הספק הרשמי של טיימאיט לניהול חשבוניות',
      logo: 'logo-sign.svg',
      connected: adminStore.businessSiteData?.paymentIntegration?.invoiceConfig?.provider === InvoiceProvider.PAYME,
      flow: [markRaw(PaymeInvoicesInstructions), markRaw(PaymeInvoicesValidate), markRaw(PaymentIntegrationSuccessStep)]
    },
    {
      id: InvoiceProvider.EZCOUNT,
      name: 'EasyCount',
      desc: 'ספק חשבונית וקבלות אונליין',
      logo: 'ezcount-logo.jpg',
      connected: adminStore.businessSiteData?.paymentIntegration?.invoiceConfig?.provider === InvoiceProvider.EZCOUNT,
      flow: [markRaw(EzCountInstructions), markRaw(EzCountFields), markRaw(PaymentIntegrationSuccessStep)]
    },
    {
      id: InvoiceProvider.SMARTBEE,
      name: 'SmartBee',
      desc: 'ספק חשבוניות ומסמכים דיגיטליים',
      logo: 'smartbee_logo.png',
      connected: adminStore.businessSiteData?.paymentIntegration?.invoiceConfig?.provider === InvoiceProvider.SMARTBEE,
      flow: [markRaw(SmartBeeInstructions), markRaw(SmartBeeFields), markRaw(PaymentIntegrationSuccessStep)]
    }
  ];
});

const getImageUrl = (imageName: string): string => {
  return new URL(`../../../../assets/img/admin/${imageName}`, import.meta.url).href;
};

const startIntegration = (provider: Provider) => {
  trackEvent('payment_integration_started');
  if (provider.connected) return;
  openDrawer(PaymentIntegrationFlow, { provider }, { title: `חיבור ${provider.name}`, full: true });
};

</script>

<style scoped>
</style>
